import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CircleStackIcon,
  ClockIcon,
  CpuChipIcon,
  ServerIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import { adminAPI, AdminHealthPayload } from '../services/api';

const AUTO_REFRESH_MS = 30_000;

type Status = 'healthy' | 'degraded' | 'unhealthy';

const statusStyles: Record<Status, string> = {
  healthy: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border-green-200 dark:border-green-800',
  degraded: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200 border-yellow-200 dark:border-yellow-800',
  unhealthy: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 border-red-200 dark:border-red-800',
};

const panelStyles: Record<Status, string> = {
  healthy: 'border-green-200 dark:border-green-800',
  degraded: 'border-yellow-200 dark:border-yellow-800',
  unhealthy: 'border-red-200 dark:border-red-800',
};

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined) return 'Unknown';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const StatusBadge: React.FC<{ status: Status }> = ({ status }) => (
  <span className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusStyles[status]}`}>
    {status}
  </span>
);

interface HealthCardProps {
  title: string;
  status: Status;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const HealthCard: React.FC<HealthCardProps> = ({ title, status, icon, children }) => (
  <div className={`rounded-xl border bg-white p-6 shadow-sm dark:bg-gray-800 ${panelStyles[status]}`}>
    <div className="mb-4 flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="rounded-lg bg-mango-100 p-2 text-mango-700 dark:bg-mango-900/30 dark:text-mango-200">
          {icon}
        </div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
      </div>
      <StatusBadge status={status} />
    </div>
    <div className="space-y-2 text-sm text-gray-700 dark:text-gray-300">{children}</div>
  </div>
);

const MetricRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div className="flex items-center justify-between gap-4 border-b border-gray-100 py-2 last:border-b-0 dark:border-gray-700">
    <span className="text-gray-500 dark:text-gray-400">{label}</span>
    <span className="text-right font-medium text-gray-900 dark:text-white">{value}</span>
  </div>
);

const AdminHealth: React.FC = () => {
  const [health, setHealth] = useState<AdminHealthPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const loadHealth = useCallback(async (showSpinner = false) => {
    try {
      if (showSpinner) setRefreshing(true);
      setError(null);
      const data = await adminAPI.getHealth();
      setHealth(data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load server health');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadHealth();
  }, [loadHealth]);

  useEffect(() => {
    if (!autoRefresh) return undefined;

    const interval = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        loadHealth();
      }
    }, AUTO_REFRESH_MS);

    return () => window.clearInterval(interval);
  }, [autoRefresh, loadHealth]);

  const checkedAt = useMemo(() => {
    if (!health?.checked_at) return 'Unknown';
    return new Date(health.checked_at).toLocaleString();
  }, [health?.checked_at]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="mx-auto h-16 w-16 animate-spin rounded-full border-b-2 border-mango-500" />
          <p className="mt-4 text-gray-600 dark:text-gray-300">Loading server health...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="bg-white shadow dark:bg-gray-800">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="flex items-center text-gray-600 transition-colors hover:text-mango-600 dark:text-gray-300 dark:hover:text-mango-400">
                <ArrowLeftIcon className="mr-1 h-5 w-5" />
                Back to Admin
              </Link>
              <div className="h-6 w-px bg-gray-300 dark:bg-gray-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Server Health</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">Owner/admin-only production diagnostics</p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {health && <StatusBadge status={health.status} />}
              <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(event) => setAutoRefresh(event.target.checked)}
                  className="rounded border-gray-300 text-mango-600 focus:ring-mango-500"
                />
                Auto-refresh 30s
              </label>
              <button
                onClick={() => loadHealth(true)}
                disabled={refreshing}
                className="inline-flex items-center rounded-lg bg-mango-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-mango-500 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <ArrowPathIcon className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh now
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
            {error}
          </div>
        )}

        {health && (
          <>
            <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-4">
                <MetricRow label="Checked at" value={checkedAt} />
                <MetricRow label="Last browser update" value={lastUpdated ? lastUpdated.toLocaleTimeString() : 'Unknown'} />
                <MetricRow label="Environment" value={health.application.environment} />
                <MetricRow label="Git commit" value={health.application.git_commit || 'Unknown'} />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              <HealthCard title="Backend API" status={health.status} icon={<ServerIcon className="h-6 w-6" />}>
                <MetricRow label="App" value={health.application.name} />
                <MetricRow label="Version" value={health.application.version} />
                <MetricRow label="Overall status" value={health.status} />
                <MetricRow label="Response cache" value={`${health.cache_ttl_seconds}s`} />
              </HealthCard>

              <HealthCard title="Database" status={health.database.status} icon={<CircleStackIcon className="h-6 w-6" />}>
                <MetricRow label="Reachable" value={health.database.reachable ? 'Yes' : 'No'} />
                <MetricRow label="Latency" value={`${health.database.latency_ms}ms`} />
                <MetricRow label="Dialect" value={health.database.dialect || 'Unknown'} />
                {health.database.active_sessions !== undefined && <MetricRow label="Active sessions" value={health.database.active_sessions} />}
                {health.database.idle_in_transaction_sessions !== undefined && <MetricRow label="Idle in transaction" value={health.database.idle_in_transaction_sessions} />}
                {health.database.lock_waiters !== undefined && <MetricRow label="Lock waiters" value={health.database.lock_waiters} />}
                {health.database.longest_query_seconds !== undefined && <MetricRow label="Longest query" value={`${health.database.longest_query_seconds}s`} />}
                {health.database.error && <MetricRow label="Error" value={health.database.error} />}
              </HealthCard>

              <HealthCard title="Redis" status={health.redis.status} icon={<SignalIcon className="h-6 w-6" />}>
                <MetricRow label="Reachable" value={health.redis.reachable ? 'Yes' : 'No'} />
                <MetricRow label="Latency" value={`${health.redis.latency_ms}ms`} />
                {health.redis.error && <MetricRow label="Error" value={health.redis.error} />}
              </HealthCard>

              <HealthCard title="System" status={health.system.status} icon={<CpuChipIcon className="h-6 w-6" />}>
                <MetricRow label="Load average" value={`${health.system.load_average['1m']} / ${health.system.load_average['5m']} / ${health.system.load_average['15m']}`} />
                <MetricRow label="Memory used" value={health.system.memory.used_percent === null ? 'Unknown' : `${health.system.memory.used_percent}%`} />
                <MetricRow label="Memory available" value={health.system.memory.available_mb === null ? 'Unknown' : `${health.system.memory.available_mb} MB`} />
                <MetricRow label="Disk used" value={`${health.system.disk.used_percent}%`} />
                <MetricRow label="Disk free" value={`${health.system.disk.free_gb} GB`} />
                <MetricRow label="Server uptime" value={formatDuration(health.system.uptime_seconds)} />
              </HealthCard>
            </div>

            <div className="mt-6 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900 dark:border-blue-800 dark:bg-blue-900/20 dark:text-blue-100">
              <div className="flex gap-3">
                <ClockIcon className="h-5 w-5 flex-none" />
                <p>
                  This dashboard only performs lightweight checks and pauses polling while the tab is hidden. Detailed logs are intentionally not exposed here to avoid leaking secrets or user data.
                </p>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default AdminHealth;
